<template>
  <div class="dashboard">

    <!-- ============================== ECRAN MOBILE ==============================
         Charte .wm-*. Le tableau de bord du bureau tient sur une grille de trois
         colonnes ; ici tout s'empile, et l'ordre devient une hierarchie : ce qui
         alerte d'abord, le chiffre qui porte l'ecran ensuite, le detail apres.
         Aucune couleur en dur : tout passe par les jetons de la charte. -->
    <div v-if="isMobile" class="wm-screen">
      <div class="wm-body">

        <!-- Un appel en echec ne doit pas se confondre avec un zero. -->
        <div v-if="errors.length" class="wm-notice"
             style="background:var(--wm-danger-tint);border-color:#f6c8d3">
          <div class="wm-notice-title" style="color:var(--wm-danger)">
            Certaines donn&eacute;es n'ont pas pu &ecirc;tre charg&eacute;es
          </div>
          <div class="wm-notice-sub" style="color:#a97b88">{{ errors.join(' &middot; ') }}</div>
          <button class="wm-notice-action"
                  style="background:#fadde4;color:var(--wm-danger)" @click="loadAll()">
            R&eacute;essayer
          </button>
        </div>

        <div v-else-if="!loading && isEmptyPeriod" class="wm-notice">
          <div class="wm-notice-title">Aucune production sur la p&eacute;riode s&eacute;lectionn&eacute;e</div>
          <div class="wm-notice-sub">
            {{ readableDate(interval.startDate, 'dmY') }} &rarr; {{ readableDate(interval.endDate, 'dmY') }}
          </div>
          <button v-if="previousPeriodLabel" class="wm-notice-action"
                  @click="$emit('period', previousPeriod)">
            &#9668; Voir {{ previousPeriodLabel }}
          </button>
        </div>

        <!-- LE chiffre de l'ecran : l'emission. Un seul en avant, sinon aucun ne ressort. -->
        <div class="wm-hero">
          <div>
            <div class="wm-hero-label">{{ kpiCards[0].label }}</div>
            <div class="wm-hero-value">{{ kpiCards[0].value }}</div>
            <div class="wm-hero-sub">{{ kpiCards[0].hint }}</div>
          </div>
          <div v-if="kpiCards[0].delta !== null" class="wm-hero-delta">
            {{ kpiCards[0].delta > 0 ? '+' : '' }}{{ kpiCards[0].delta }}%
          </div>
        </div>

        <!-- Les autres indicateurs, a plat. Chacun mene a son ecran. -->
        <div class="wm-grid">
          <component v-for="kpi in kpiCards.slice(1)" :key="'mk_' + kpi.label"
                     :is="kpi.route ? 'router-link' : 'div'" :to="kpi.route"
                     class="wm-stat" style="text-decoration:none;color:inherit">
            <div class="wm-stat-value">{{ kpi.value }}</div>
            <div class="wm-stat-label">{{ kpi.label }}</div>
            <div class="wm-stat-sub">{{ kpi.hint }}</div>
          </component>
        </div>

        <!-- Funnel : chaque cotation ne porte que son etape la plus avancee, les
             barres cumulent donc les etapes suivantes (cf. funnelSteps). -->
        <div class="wm-card">
          <div class="wm-card-head" style="margin-bottom:12px">
            <div>
              <div class="wm-card-title">Funnel Cotations</div>
              <div class="wm-card-sub">Cotations ayant atteint chaque &eacute;tape</div>
            </div>
            <span class="wm-tag wm-tag--accent">{{ funnelTotal }} cotations</span>
          </div>
          <div style="display:flex;flex-direction:column;gap:11px">
            <div v-for="step in funnelSteps" :key="'mf_' + step.status">
              <div style="display:flex;justify-content:space-between;align-items:baseline">
                <span style="font-size:13px">{{ step.label }}</span>
                <span class="wm-mono" style="font-size:12px;color:var(--wm-ink-3)">
                  {{ step.reached }} &middot; {{ step.rate }}%
                </span>
              </div>
              <div class="wm-progress" style="margin-top:6px">
                <div class="wm-progress-bar" :style="{width: step.rate + '%'}"></div>
              </div>
            </div>
          </div>
          <div style="margin-top:14px;padding-top:12px;border-top:1px solid var(--wm-hairline);display:flex;flex-direction:column;gap:7px;font-size:12px">
            <div style="display:flex;justify-content:space-between">
              <span style="color:var(--wm-ink-3)">Abandonn&eacute;es</span>
              <span style="color:var(--wm-danger);font-weight:600">{{ funnelAborted }}</span>
            </div>
            <div style="display:flex;justify-content:space-between">
              <span style="color:var(--wm-ink-3)">Expir&eacute;es</span>
              <span style="font-weight:600">{{ funnelExpired }}</span>
            </div>
            <div style="display:flex;justify-content:space-between">
              <span style="color:var(--wm-ink-3)">Taux conversion global</span>
              <span style="color:var(--wm-primary-ink);font-weight:600">{{ conversionRate }}%</span>
            </div>
          </div>
        </div>

        <!-- Renouvellements -->
        <div class="wm-card">
          <div class="wm-card-title">Renouvellements</div>
          <div class="wm-card-sub">Contrats arrivant &agrave; &eacute;ch&eacute;ance sur la p&eacute;riode</div>
          <div class="wm-grid" style="margin-top:14px;gap:8px">
            <div class="wm-tile wm-tile--danger">
              <div class="wm-tile-value">{{ renewalKpis.pending }}</div>
              <div class="wm-tile-label">A traiter</div>
            </div>
            <div class="wm-tile wm-tile--warning">
              <div class="wm-tile-value">{{ renewalKpis.inProgress }}</div>
              <div class="wm-tile-label">Devis en cours</div>
            </div>
            <div class="wm-tile wm-tile--neutral">
              <div class="wm-tile-value">{{ renewalKpis.churned }}</div>
              <div class="wm-tile-label">Perdus (churn)</div>
            </div>
            <div class="wm-tile wm-tile--primary">
              <div class="wm-tile-value">{{ renewalKpis.renewed }}</div>
              <div class="wm-tile-label">Renouvel&eacute;s</div>
            </div>
          </div>
          <div style="margin-top:14px;display:flex;align-items:center;gap:14px;background:#fbfdff;border:1px solid var(--wm-hairline);border-radius:14px;padding:14px">
            <div style="width:58px;height:58px;border-radius:50%;border:5px solid var(--wm-hairline);display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:var(--wm-primary-ink);flex:0 0 auto">
              {{ renewalKpis.rate }}%
            </div>
            <div style="font-size:12px;color:var(--wm-ink-2)">
              Taux de renouvellement sur la p&eacute;riode s&eacute;lectionn&eacute;e
            </div>
          </div>
        </div>

        <!-- Pipeline des avenants -->
        <div class="wm-card">
          <div class="wm-card-title">Pipeline Avenants</div>
          <div class="wm-card-sub">Avenants dont la date d'effet tombe sur la p&eacute;riode</div>
          <div style="display:flex;align-items:center;justify-content:space-between;margin-top:16px">
            <div style="text-align:center;flex:1">
              <div class="wm-mono" style="font-size:20px;font-weight:600;color:var(--wm-primary-ink)">
                {{ productionKpis.global.projets }}
              </div>
              <div style="font-size:11px;color:var(--wm-ink-3)">Projets</div>
            </div>
            <span style="color:#cdd6e2;font-size:12px">&rarr;</span>
            <div style="text-align:center;flex:1">
              <div class="wm-mono" style="font-size:20px;font-weight:600;color:var(--wm-warning)">
                {{ productionKpis.global.enAttente }}
              </div>
              <div style="font-size:11px;color:var(--wm-ink-3)">Valides</div>
            </div>
            <span style="color:#cdd6e2;font-size:12px">&rarr;</span>
            <div style="text-align:center;flex:1">
              <div class="wm-mono" style="font-size:20px;font-weight:600">
                {{ productionKpis.global.emis }}
              </div>
              <div style="font-size:11px;color:var(--wm-ink-3)">Emis</div>
            </div>
          </div>
          <div style="margin-top:14px;background:#f0f8ff;border-radius:12px;padding:11px 13px;display:flex;justify-content:space-between;align-items:center">
            <span style="font-size:12px;color:var(--wm-ink-2)">Taux d'&eacute;mission</span>
            <span style="font-size:13px;font-weight:600">{{ productionKpis.global.tauxEmission }}%</span>
          </div>
        </div>

        <!-- Emission par branche -->
        <div class="wm-card">
          <div class="wm-card-head">
            <div class="wm-card-title">Emission par branche</div>
            <div class="wm-mono" style="font-size:13px;font-weight:600;color:var(--wm-primary-ink)">
              {{ minify(emissionGlobale) }}
            </div>
          </div>
          <div style="margin-top:12px;display:flex;flex-direction:column">
            <div v-if="portfolioBranches.length === 0" class="wm-empty">
              Aucune &eacute;mission sur la p&eacute;riode
            </div>
            <div v-for="(branch, i) in portfolioBranches" :key="'mb_' + branch.label"
                 style="display:flex;align-items:center;gap:9px;padding:11px 0;border-bottom:1px solid var(--wm-hairline)">
              <span :style="{background: branchColors[i % branchColors.length]}"
                    style="width:7px;height:7px;border-radius:50%;flex:0 0 auto"></span>
              <span style="font-size:13px;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
                {{ branch.label }}
              </span>
              <span style="text-align:right">
                <span class="wm-mono" style="font-size:13px;font-weight:600;display:block">
                  {{ minify(branch.amount) }}
                </span>
                <span style="font-size:10px;color:var(--wm-ink-5)">{{ branch.share }}%</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Encaissement -->
        <div class="wm-card">
          <div class="wm-card-head">
            <div class="wm-card-title">Encaissement</div>
            <router-link :to="{name: 'payment-list'}"
                         style="font-size:12px;color:var(--wm-primary-ink);font-weight:600;text-decoration:none">
              D&eacute;tail &rarr;
            </router-link>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:baseline;margin-top:12px">
            <span style="font-size:12px;color:var(--wm-ink-2)">Taux d'encaissement</span>
            <span style="font-size:15px;font-weight:700"
                  :style="{color: cashing.rate >= 80 ? 'var(--wm-success)' : 'var(--wm-danger)'}">
              {{ cashing.rate }}%
            </span>
          </div>
          <div class="wm-progress" style="height:4px;margin-top:8px">
            <div class="wm-progress-bar" :style="{width: cashing.rate + '%'}"></div>
          </div>
          <div class="wm-grid" style="margin-top:12px;gap:8px">
            <div class="wm-tile wm-tile--success" style="text-align:center;padding:12px">
              <div class="wm-tile-value">{{ minify(cashing.cashed) }}</div>
              <div class="wm-tile-label">Encaiss&eacute;</div>
            </div>
            <div class="wm-tile wm-tile--danger" style="text-align:center;padding:12px">
              <div class="wm-tile-value">{{ minify(cashing.remaining) }}</div>
              <div class="wm-tile-label">Reste &agrave; encaisser</div>
            </div>
          </div>
        </div>

        <!-- Alertes : la liste reelle, ou le fait qu'il n'y en ait aucune. -->
        <div class="wm-card" v-if="alertsList.length === 0"
             style="display:flex;align-items:center;gap:12px;padding:20px">
          <div style="width:34px;height:34px;border-radius:50%;background:var(--wm-success-tint);color:var(--wm-success);display:flex;align-items:center;justify-content:center;font-size:15px;flex:0 0 auto">
            <i class="fad fa-check"></i>
          </div>
          <div>
            <div style="font-size:13px;font-weight:600">Aucune alerte en cours</div>
            <div style="font-size:11px;color:var(--wm-ink-4);margin-top:2px">Tout est &agrave; jour</div>
          </div>
        </div>
        <div class="wm-card" v-else>
          <div class="wm-card-head" style="margin-bottom:12px">
            <div class="wm-card-title">Alertes</div>
            <span class="wm-badge wm-badge--danger">{{ alertsList.length }}</span>
          </div>
          <div v-for="(alert, i) in alertsList" :key="'ma_' + i"
               style="display:flex;gap:10px;padding:10px 0;border-bottom:1px solid var(--wm-hairline)">
            <i :class="alert.icon" style="color:var(--wm-danger);font-size:15px;margin-top:2px"></i>
            <div style="min-width:0">
              <div style="font-size:13px;font-weight:600">{{ alert.title }}</div>
              <div style="font-size:11px;color:var(--wm-ink-4);margin-top:2px">{{ alert.description }}</div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ============================== ECRAN BUREAU ============================== -->
    <template v-if="!isMobile">
    <!-- Bandeau d'erreur : un appel qui echoue ne doit pas se confondre avec un zero -->
    <div v-if="errors.length" class="alert alert-danger d-flex align-items-center mb-5">
      <i class="fad fa-triangle-exclamation fs-2 me-3"></i>
      <div class="flex-grow-1">
        <div class="fw-bold">Certaines donnees n'ont pas pu etre chargees</div>
        <div class="fs-8">{{ errors.join(' · ') }}</div>
      </div>
      <div class="btn btn-sm btn-light-danger" @click="loadAll()">
        <i class="fad fa-rotate-right me-1"></i> Reessayer
      </div>
    </div>

    <!-- Periode sans production : le dire, et proposer la periode precedente -->
    <div v-else-if="!loading && isEmptyPeriod"
         class="alert alert-light-warning border border-warning border-dashed d-flex align-items-center mb-5">
      <i class="fad fa-calendar-xmark fs-2 text-warning me-3"></i>
      <div class="flex-grow-1">
        <div class="fw-bold text-gray-800">Aucune production sur la periode selectionnee</div>
        <div class="fs-8 text-gray-600">
          {{ readableDate(interval.startDate, 'dmY') }} &rarr; {{ readableDate(interval.endDate, 'dmY') }}
        </div>
      </div>
      <div v-if="previousPeriodLabel" class="btn btn-sm btn-light-warning" @click="$emit('period', previousPeriod)">
        <i class="fad fa-backward me-1"></i> Voir {{ previousPeriodLabel }}
      </div>
    </div>

    <!-- ROW 1 : KPI Cards -->
    <div class="row g-5 mb-5">
      <div class="col-6 col-md-4 col-xxl-2" v-for="kpi in kpiCards" :key="kpi.label">
        <component :is="kpi.route ? 'router-link' : 'div'" v-bind="kpi.route ? { to: kpi.route } : {}"
                   class="card card-flush h-100 border-0 shadow-sm text-decoration-none"
                   :class="[kpi.active ? 'bg-' + kpi.color : '', kpi.route ? 'cursor-pointer card-hover' : '']">
          <div class="card-body p-4 d-flex flex-column">
            <div class="d-flex align-items-center mb-2">
              <div class="d-flex align-items-center justify-content-center rounded-circle me-3"
                   :class="kpi.active ? 'bg-white bg-opacity-20' : 'bg-light-' + kpi.color"
                   style="width: 40px; height: 40px; min-width: 40px;">
                <i :class="kpi.icon + ' fs-3 text-' + kpi.color"
                   :style="kpi.active ? 'color: white !important' : ''"></i>
              </div>
              <span v-if="kpi.delta !== null && kpi.delta !== undefined"
                    class="badge badge-sm ms-auto fw-bold"
                    :class="deltaClass(kpi.delta, kpi.active)">
                <i class="fad fs-9 me-1" :class="kpi.delta >= 0 ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down'"></i>
                {{ kpi.delta >= 0 ? '+' : '' }}{{ kpi.delta }}%
              </span>
            </div>
            <div v-if="loading" class="skeleton-line w-75 my-2"></div>
            <div v-else class="fs-2x fw-bold lh-1 text-nowrap"
                 :class="kpi.active ? 'text-white' : 'text-gray-800'">
              {{ kpi.value }}<span v-if="kpi.suffix" class="fs-7 ms-1">{{ kpi.suffix }}</span>
            </div>
            <span class="fw-semibold fs-8 mt-1"
                  :class="kpi.active ? 'text-white text-opacity-75' : 'text-gray-500'">
              {{ kpi.label }}
            </span>
            <span v-if="kpi.hint" class="fs-9 mt-1"
                  :class="kpi.active ? 'text-white text-opacity-75' : 'text-gray-400'">
              {{ kpi.hint }}
            </span>
          </div>
        </component>
      </div>
    </div>

    <!-- ROW 2 : Production Chart + Portefeuille -->
    <div class="row g-5 mb-5">
      <div class="col-xl-8">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800 d-block">
              <i class="fad fa-chart-line text-primary me-2"></i>
              Production
              <span class="d-block fs-8 fw-normal text-gray-500 mt-1">
                Primes emises par {{ granularityLabel.toLowerCase() }}, avec le cumul sur la periode
              </span>
            </h3>
            <div class="card-toolbar">
              <span class="fw-bold fs-4 text-primary">{{ currency(emissionGlobale) }}</span>
            </div>
          </div>
          <div class="card-body pt-0">
            <div v-if="loading" class="skeleton-line" style="height:320px"></div>
            <apexchart v-else-if="productionChart.series.length" type="line" height="320"
                       :options="productionChart" :series="productionChart.series"></apexchart>
            <div v-else class="d-flex flex-column justify-content-center align-items-center" style="height:320px">
              <i class="fad fa-chart-line fs-3x text-gray-300 mb-3"></i>
              <span class="text-gray-400 fs-7">Aucune emission sur la periode</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl-4">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800">
              <i class="fad fa-chart-pie text-info me-2"></i>
              Portefeuille
            </h3>
          </div>
          <div class="card-body pt-0 d-flex flex-column">
            <apexchart v-if="portfolioChart.series.length" type="donut" height="200"
                       :options="portfolioChart" :series="portfolioChart.series"></apexchart>
            <div v-else-if="!loading" class="text-center py-10">
              <i class="fad fa-chart-pie fs-3x text-gray-300 mb-3 d-block"></i>
              <span class="text-gray-400 fs-8">Aucune emission a repartir</span>
            </div>
            <div class="mt-auto">
              <div v-for="(branch, i) in portfolioBranches" :key="branch.label"
                   class="d-flex align-items-center mb-3">
                <div class="bullet w-12px h-8px rounded-pill me-3"
                     :style="{ background: branchColors[i % branchColors.length] }"></div>
                <span class="text-gray-600 fs-7 flex-grow-1 text-truncate">{{ branch.label }}</span>
                <span class="text-gray-400 fs-9 me-2">{{ branch.share }}%</span>
                <span class="fw-bold text-gray-800 fs-7">{{ miniCurrency(branch.amount) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ROW 3 : Funnel Cotations + Renouvellements + Pipeline Avenants -->
    <div class="row g-5 mb-5">
      <!-- Funnel Cotations (Form API) -->
      <div class="col-xl-4">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800 d-block">
              <i class="fad fa-filter text-info me-2"></i>
              Funnel Cotations
              <span class="d-block fs-9 fw-normal text-gray-500 mt-1">
                Cotations ayant atteint chaque etape
              </span>
            </h3>
            <div class="card-toolbar">
              <span class="badge badge-light-info fs-8 fw-bold px-3 py-2">
                {{ funnelTotal }} cotations
              </span>
            </div>
          </div>
          <div class="card-body pt-2">
            <div class="d-flex flex-column gap-3">
              <div v-for="(step, i) in funnelSteps" :key="step.status">
                <div class="d-flex justify-content-between mb-1">
                  <span class="text-gray-600 fs-7">
                    {{ step.label }}
                    <span v-if="i > 0 && step.stepRate !== null"
                          class="fs-9 ms-1"
                          :class="step.stepRate >= 50 ? 'text-success' : step.stepRate >= 20 ? 'text-warning' : 'text-danger'">
                      &darr;{{ step.stepRate }}%
                    </span>
                  </span>
                  <div>
                    <span class="fw-bold fs-7 me-2" :class="'text-' + step.color">{{ step.reached }}</span>
                    <span class="badge badge-sm badge-light">{{ step.rate }}%</span>
                  </div>
                </div>
                <div class="h-8px bg-light rounded-pill overflow-hidden">
                  <div class="h-100 rounded-pill" :class="'bg-' + step.color"
                       :style="{ width: step.rate + '%' }"></div>
                </div>
              </div>
            </div>
            <div class="separator my-4"></div>
            <div class="d-flex flex-column gap-2">
              <div class="d-flex justify-content-between">
                <span class="text-gray-500 fs-8">Abandonnees</span>
                <span class="fw-bold text-danger fs-8">{{ funnelAborted }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span class="text-gray-500 fs-8">Expirees</span>
                <span class="fw-bold text-gray-400 fs-8">{{ funnelExpired }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span class="text-gray-500 fs-8">Taux conversion global</span>
                <span class="fw-bold text-primary fs-7">{{ conversionRate }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Renouvellements -->
      <div class="col-xl-4">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800 d-block">
              <i class="fad fa-sync text-warning me-2"></i>
              Renouvellements
              <span class="d-block fs-9 fw-normal text-gray-500 mt-1">
                Contrats arrivant a echeance sur la periode
              </span>
            </h3>
            <div class="card-toolbar" v-if="renewalKpis.pending > 0">
              <router-link :to="{ name: 'renewal-dashboard' }"
                           class="badge badge-light-danger fs-8 fw-bold px-3 py-2">
                {{ renewalKpis.pending }} a traiter
              </router-link>
            </div>
          </div>
          <div class="card-body pt-2">
            <div class="d-flex flex-column gap-4">
              <div v-for="item in renewalItems" :key="item.label">
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-gray-600 fs-7">{{ item.label }}</span>
                  <span class="fw-bold fs-7" :class="'text-' + item.color">{{ item.value }}</span>
                </div>
                <div class="h-6px bg-light rounded-pill overflow-hidden">
                  <div class="h-100 rounded-pill" :class="'bg-' + item.color"
                       :style="{ width: item.percent + '%' }"></div>
                </div>
              </div>
            </div>
            <div class="separator my-5"></div>
            <div class="text-center">
              <apexchart type="radialBar" height="180" :options="renewalGauge" :series="[renewalKpis.rate]"></apexchart>
              <div class="text-gray-500 fs-7 mt-n2">Taux de renouvellement</div>
              <div v-if="renewalKpis.coverageRate > renewalKpis.rate" class="text-gray-400 fs-9 mt-1">
                {{ renewalKpis.coverageRate }}% pris en charge (devis compris)
              </div>
            </div>
            <div v-if="renewalKpis.primeEnRisque > 0" class="bg-light-warning rounded-3 p-3 mt-3 text-center">
              <span class="text-gray-600 fs-9 d-block">Prime non encore renouvelee</span>
              <span class="text-warning fs-5 fw-bold">{{ miniCurrency(renewalKpis.primeEnRisque) }}</span>
            </div>
            <div v-if="renewalKpis.injoignable > 0" class="bg-light-danger rounded-3 p-3 mt-3 text-center">
              <i class="fad fa-phone-slash text-danger me-1"></i>
              <span class="text-danger fs-8 fw-bold">{{ renewalKpis.injoignable }} clients injoignables</span>
              <span class="text-gray-500 fs-9 d-block">({{ renewalKpis.injoignableRate }}% du portefeuille)</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pipeline Avenants -->
      <div class="col-xl-4">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800 d-block">
              <i class="fad fa-layer-group text-primary me-2"></i>
              Pipeline Avenants
              <span class="d-block fs-9 fw-normal text-gray-500 mt-1">
                Avenants dont la date d'effet tombe sur la periode
              </span>
            </h3>
          </div>
          <div class="card-body pt-0">
            <!-- Production KPIs -->
            <div class="d-flex align-items-center justify-content-center mb-3 mt-3">
              <span class="fw-bold fs-2 text-gray-800">{{ productionKpis.global.total }}</span>
              <span class="text-gray-400 fs-7 ms-2">avenants</span>
            </div>
            <div class="d-flex justify-content-between align-items-end mb-5">
              <div class="text-center flex-1">
                <div class="fw-bold fs-2 text-primary">{{ productionKpis.global.projets }}</div>
                <div class="text-gray-400 fs-8">Projets</div>
              </div>
              <div class="text-center" style="width:20px">
                <i class="fad fa-arrow-right text-gray-300"></i>
              </div>
              <div class="text-center flex-1">
                <div class="fw-bold fs-2 text-warning">{{ productionKpis.global.enAttente }}</div>
                <div class="text-gray-400 fs-8">Valides</div>
              </div>
              <div class="text-center" style="width:20px">
                <i class="fad fa-arrow-right text-gray-300"></i>
              </div>
              <div class="text-center flex-1">
                <div class="fw-bold fs-2 text-success">{{ productionKpis.global.emis }}</div>
                <div class="text-gray-400 fs-8">Emis</div>
                <div v-if="productionKpis.global.expires > 0" class="text-gray-400 fs-9">
                  dont {{ productionKpis.global.expires }} expires
                </div>
              </div>
              <div class="text-center flex-1" v-if="pipelineAutres > 0">
                <div class="fw-bold fs-2 text-gray-400">{{ pipelineAutres }}</div>
                <div class="text-gray-400 fs-8">Autres</div>
              </div>
            </div>

            <div class="bg-light-primary rounded-3 p-4 mb-5">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="text-gray-600 fs-7">Taux d'emission</span>
                <span class="fw-bold text-primary">{{ productionKpis.global.tauxEmission }}%</span>
              </div>
              <div class="h-8px bg-white rounded-pill overflow-hidden">
                <div class="h-100 bg-primary rounded-pill"
                     :style="{ width: productionKpis.global.tauxEmission + '%' }"></div>
              </div>
            </div>

            <!-- Par type -->
            <div class="fw-semibold text-gray-600 fs-7 mb-3">Par type</div>
            <div v-for="t in mergedByType" :key="t.type"
                 class="d-flex align-items-center justify-content-between py-2 px-3 border-bottom border-dashed">
              <span class="badge" :class="typeBadge(t.type)">{{ typeLabel(t.type) }}</span>
              <div class="text-end">
                <div>
                  <span class="fw-bold text-success fs-7">{{ t.emis }}</span>
                  <span class="text-gray-400 fs-8"> / {{ t.total }}</span>
                </div>
                <div class="text-gray-400 fs-9">{{ miniCurrency(t.primeEmise) }}</div>
              </div>
            </div>

            <!-- Par branche -->
            <div class="fw-semibold text-gray-600 fs-7 mb-3 mt-4">Par branche</div>
            <div v-for="b in productionKpis.byBranch" :key="b.branchSlug"
                 class="d-flex align-items-center justify-content-between py-2 px-3 border-bottom border-dashed">
              <span class="text-gray-700 fs-7">{{ b.branch || 'Non renseignee' }}</span>
              <div>
                <span class="fw-bold text-success fs-7">{{ b.emis }}</span>
                <span class="text-gray-400 fs-8"> / {{ b.total }}</span>
                <span class="badge badge-sm ms-1"
                      :class="b.rate >= 50 ? 'badge-light-success' : 'badge-light-danger'">
                  {{ b.rate }}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ROW 4 : Emissions + Commissions + Encaissement -->
    <div class="row g-5 mb-5">
      <!-- Emissions par branche -->
      <div class="col-xl-4">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800">
              <i class="fad fa-arrow-up text-primary me-2"></i>
              Emissions Globale
            </h3>
            <div class="card-toolbar">
              <span class="fw-bold fs-3 text-primary">{{ currency(emissionGlobale) }}</span>
            </div>
          </div>
          <div class="card-body pt-2">
            <b-tabs pills content-class="mt-3" nav-class="nav-light-primary">
              <b-tab v-for="category in categories" :key="category.uuid" :title="category.label">
                <div v-for="branch in category.branches" :key="branch.uuid"
                     class="d-flex align-items-center py-3 px-3 border-bottom border-dashed">
                  <div class="bullet w-10px h-6px rounded-pill me-3" :style="{ background: branch.color }"></div>
                  <router-link :to="{ name: 'income-state', query: { branch: branch.slug } }"
                               class="text-gray-700 fw-semibold text-hover-primary fs-7 flex-grow-1">
                    {{ branch.label }}
                  </router-link>
                  <div class="text-end">
                    <div class="fw-bold text-gray-800 fs-7">{{ miniCurrency(branch.emission) }}</div>
                    <div class="text-gray-400 fs-9">{{ branch.count }} avenants</div>
                  </div>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>

      <!-- Commissions -->
      <div class="col-xl-4">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800 d-block">
              <i class="fad fa-percentage text-success me-2"></i>
              Commissions
              <span v-if="emissionGlobale > 0" class="d-block fs-9 fw-normal text-gray-500 mt-1">
                Taux moyen : {{ commissionRate }}% de l'emission
              </span>
            </h3>
            <div class="card-toolbar">
              <span class="fw-bold fs-3 text-success">{{ currency(commissionGlobale) }}</span>
            </div>
          </div>
          <div class="card-body pt-2">
            <b-tabs pills content-class="mt-3" nav-class="nav-light-success">
              <b-tab v-for="category in categories" :key="'com-' + category.uuid" :title="category.label">
                <div v-for="branch in category.branches" :key="'com-' + branch.uuid"
                     class="d-flex align-items-center py-3 px-3 border-bottom border-dashed">
                  <div class="bullet w-10px h-6px rounded-pill me-3" :style="{ background: branch.color }"></div>
                  <span class="text-gray-700 fs-7 flex-grow-1">{{ branch.label }}</span>
                  <span class="fw-bold text-gray-800 fs-7">{{ miniCurrency(branch.commissions) }}</span>
                </div>
              </b-tab>
            </b-tabs>
          </div>
        </div>
      </div>

      <!-- Encaissement -->
      <div class="col-xl-4">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800 d-block">
              <i class="fad fa-money-bill-wave text-info me-2"></i>
              Encaissement
              <span class="d-block fs-9 fw-normal text-gray-500 mt-1">
                Sur les primes emises de la periode
              </span>
            </h3>
            <div class="card-toolbar">
              <router-link :to="{ name: 'payment-list' }" class="btn btn-icon btn-light-info btn-xs">
                <i class="fad fa-arrow-right"></i>
              </router-link>
            </div>
          </div>
          <div class="card-body pt-2">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span class="text-gray-600 fs-7">Taux d'encaissement</span>
              <span class="fw-bold fs-3" :class="cashing.rate >= 80 ? 'text-success' : cashing.rate >= 50 ? 'text-warning' : 'text-danger'">
                {{ cashing.rate }}%
              </span>
            </div>
            <div class="h-10px bg-light rounded-pill overflow-hidden mb-5">
              <div class="h-100 rounded-pill"
                   :class="cashing.rate >= 80 ? 'bg-success' : cashing.rate >= 50 ? 'bg-warning' : 'bg-danger'"
                   :style="{ width: Math.min(100, cashing.rate) + '%' }"></div>
            </div>

            <div class="d-flex gap-3 mb-5">
              <div class="bg-light-success rounded-3 p-3 flex-1 text-center">
                <div class="fw-bold fs-4 text-success">{{ miniCurrency(cashing.cashed) }}</div>
                <div class="text-gray-500 fs-9">Encaisse</div>
              </div>
              <div class="bg-light-danger rounded-3 p-3 flex-1 text-center">
                <div class="fw-bold fs-4 text-danger">{{ miniCurrency(cashing.remaining) }}</div>
                <div class="text-gray-500 fs-9">Reste a encaisser</div>
              </div>
            </div>

            <template v-if="isManager">
              <div class="separator my-4"></div>
              <div class="fw-semibold text-gray-600 fs-7 mb-3">Factures de la periode</div>
              <div class="d-flex gap-3 mb-4">
                <div class="bg-light-success rounded-3 p-3 flex-1 text-center">
                  <div class="fw-bold fs-4 text-success">{{ invoicePaid.count }}</div>
                  <div class="text-gray-500 fs-9">Payees</div>
                  <div class="text-success fs-9 fw-semibold">{{ miniCurrency(invoicePaid.amount) }}</div>
                </div>
                <div class="bg-light-danger rounded-3 p-3 flex-1 text-center">
                  <div class="fw-bold fs-4 text-danger">{{ invoiceUnpaid.count }}</div>
                  <div class="text-gray-500 fs-9">Impayees</div>
                  <div class="text-danger fs-9 fw-semibold">{{ miniCurrency(invoiceUnpaid.amount) }}</div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ROW 5 : Top Partenaires + Alertes -->
    <div class="row g-5 mb-5">
      <!-- Top Partenaires (Manager only) -->
      <div class="col-xl-6" v-if="isManager">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800">
              <i class="fad fa-trophy text-warning me-2"></i>
              Top Partenaires
            </h3>
          </div>
          <div class="card-body pt-0">
            <div class="table-responsive">
              <table class="table table-row-bordered align-middle gy-4 gs-3">
                <thead>
                  <tr class="fw-semibold text-gray-500 fs-8 text-uppercase">
                    <th>#</th>
                    <th>Partenaire</th>
                    <th class="text-end">Total</th>
                    <th class="text-end">Emis</th>
                    <th class="text-end">Prime emise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(partner, i) in productionKpis.byPartner" :key="partner.partnerCode">
                    <td>
                      <span class="badge badge-circle fw-bold fs-8"
                            :class="i < 3 ? 'badge-warning text-white' : 'badge-light text-gray-600'"
                            style="width: 28px; height: 28px;">
                        {{ i + 1 }}
                      </span>
                    </td>
                    <td>
                      <div class="fw-semibold text-gray-800">{{ partner.partnerName || partner.partnerCode }}</div>
                      <div class="text-gray-400 fs-9">{{ partner.partnerCode }}</div>
                    </td>
                    <td class="text-end fw-bold text-gray-700">{{ partner.total }}</td>
                    <td class="text-end fw-bold text-success">{{ partner.emis }}</td>
                    <td class="text-end fw-bold text-gray-700">{{ miniCurrency(partner.primeEmise) }}</td>
                  </tr>
                  <tr v-if="productionKpis.byPartner.length === 0">
                    <td colspan="5" class="text-center text-gray-400 py-5">Aucune donnee</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Alertes -->
      <div :class="isManager ? 'col-xl-6' : 'col-xl-12'">
        <div class="card card-flush shadow-sm h-100">
          <div class="card-header border-0 pt-5">
            <h3 class="card-title fw-bold text-gray-800">
              <i class="fad fa-bell text-danger me-2"></i>
              Alertes
            </h3>
            <div class="card-toolbar" v-if="alertsList.length">
              <span class="badge badge-light-danger fs-8 fw-bold px-3 py-2">{{ alertsList.length }}</span>
            </div>
          </div>
          <div class="card-body pt-2">
            <div v-for="(alert, i) in alertsList" :key="i"
                 class="d-flex align-items-start p-4 rounded-3 mb-3"
                 :class="['bg-light-' + alert.color, alert.route ? 'cursor-pointer' : '']"
                 @click="alert.route ? $router.push(alert.route) : null">
              <div class="me-3">
                <i :class="alert.icon + ' fs-3 text-' + alert.color"></i>
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold fs-7" :class="'text-' + alert.color">{{ alert.title }}</div>
                <div class="text-gray-600 fs-8 mt-1">{{ alert.description }}</div>
              </div>
              <div v-if="alert.route" class="ms-2 align-self-center">
                <i class="fad fa-chevron-right text-gray-400"></i>
              </div>
            </div>
            <div v-if="alertsList.length === 0" class="text-center py-10">
              <i class="fad fa-check-circle fs-3x text-success mb-3 d-block"></i>
              <div class="text-gray-500 fs-7">Aucune alerte en cours</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {api} from "@/services/Api";
import {helper} from "@/services/Helper";
import {auth} from "@/services/Auth";
import BranchCategory from "@/entity/BranchCategory";
import Branch from "@/entity/Branch";
import MobileAware from "@/mixins/MobileAware";

@Component({
  // `mixins` en OPTION : `extends mixins(...)` fait perdre le typage de Vue.
  mixins: [MobileAware],
})
export default class InsuranceDashboard extends Vue {
  // Fourni par le mixin MobileAware (declaration de type seulement).
  isMobile!: boolean;

  /** Montant abrege, comme sur les cartes du bureau. */
  minify(amount: number): string {
    return helper.minifyCurrency(amount || 0);
  }

  @Prop() interval!: any
  @Prop() filter?: any

  get isManager(): boolean {
    return auth.hasRole('ROLE_MANAGER')
  }

  // --- State ---
  loading = false
  errors: string[] = []

  categories: BranchCategory[] = []
  emissionGlobale = 0
  emissionCount = 0
  commissionGlobale = 0
  previousEmission = { emissionTTC: 0, count: 0, start: '', end: '' }
  cashing = { billed: 0, cashed: 0, remaining: 0, rate: 0 }
  portfolioBranches: { label: string, amount: number, share: number }[] = []
  branchColors = ['#3699FF', '#50CD89', '#FFC700', '#F1416C', '#7239EA', '#8950FC', '#1BC5BD', '#FFA800']

  // Funnel (Form API)
  funnelData: Record<string, number> = {}
  funnelTotal = 0
  funnelAborted = 0
  funnelExpired = 0

  // Renewal KPIs
  renewalKpis = {
    total: 0, renewed: 0, inProgress: 0, churned: 0, standby: 0, pending: 0,
    rate: 0, coverageRate: 0, primeEnRisque: 0,
    injoignable: 0, injoignableRate: 0, avgRenewalDays: null as number | null,
    byBranch: [] as any[]
  }

  // Production KPIs
  productionKpis = {
    global: { total: 0, emis: 0, expires: 0, enAttente: 0, projets: 0, tauxEmission: 0, primeEmise: 0 },
    previous: { total: 0, emis: 0, primeEmise: 0 },
    byType: [] as any[],
    byBranch: [] as any[],
    byPartner: [] as any[]
  }

  // CRM Data
  crmData = {
    contracts: { active: 0, toRenew: 0, deleted: 0, total: 0, byBranch: [] as any[] },
    invoices: { paid: null as any, unpaid: null as any }
  }

  // Alerts
  coreAlerts = { coveragesPending: 0, attestationErrors: 0, expiringNoRenewal: 0 }

  // Charts
  productionChart: any = { series: [], chart: { type: 'line', height: 320 } }
  portfolioChart: any = { series: [], chart: { type: 'donut', height: 200 } }

  // --- Computed ---

  /** Variation en % entre la periode et la precedente ; null si la precedente est vide. */
  variation(current: number, previous: number): number | null {
    if (!previous) return null
    return Math.round(((current - previous) / previous) * 100)
  }

  deltaClass(delta: number, active: boolean): string {
    if (active) return 'bg-white bg-opacity-20 text-white'
    return delta >= 0 ? 'badge-light-success' : 'badge-light-danger'
  }

  get isEmptyPeriod(): boolean {
    return this.emissionGlobale === 0
      && this.productionKpis.global.total === 0
      && this.funnelTotal === 0
  }

  get previousPeriod(): any {
    const start = new Date(this.interval.startDate)
    const end = new Date(this.interval.endDate)
    const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000))
    const prevEnd = new Date(start.getTime() - 86400000)
    const prevStart = new Date(prevEnd.getTime() - days * 86400000)
    return { startDate: prevStart, endDate: prevEnd }
  }

  get previousPeriodLabel(): string {
    const p = this.previousPeriod
    return helper.readable(p.startDate, 'dmY') + ' - ' + helper.readable(p.endDate, 'dmY')
  }

  get commissionRate(): string {
    if (!this.emissionGlobale) return '0'
    return ((this.commissionGlobale / this.emissionGlobale) * 100).toFixed(1)
  }

  get kpiCards() {
    const g = this.productionKpis.global
    const prev = this.productionKpis.previous
    return [
      {
        icon: 'fad fa-coins', label: 'Emission TTC',
        value: helper.minifyCurrency(this.emissionGlobale), color: 'primary', active: true,
        hint: this.emissionCount + ' avenants emis',
        delta: this.variation(this.emissionGlobale, this.previousEmission.emissionTTC),
        route: { name: 'income-state' },
      },
      {
        icon: 'fad fa-money-bill-wave', label: 'Encaisse',
        value: helper.minifyCurrency(this.cashing.cashed), color: 'success',
        hint: this.cashing.rate + '% de l\'emission',
        delta: null,
        route: { name: 'payment-list' },
      },
      {
        icon: 'fad fa-file-plus', label: 'Avenants emis',
        value: g.emis, color: 'info',
        hint: g.tauxEmission + '% de ' + g.total,
        delta: this.variation(g.emis, prev.emis),
        route: { name: 'avenant-list' },
      },
      {
        icon: 'fad fa-percentage', label: 'Commissions',
        value: helper.minifyCurrency(this.commissionGlobale), color: 'success',
        hint: this.commissionRate + '% de l\'emission',
        delta: null,
        route: null,
      },
      {
        icon: 'fad fa-file-signature', label: 'Cotations validees',
        value: this.funnelData['VALIDATED'] || 0, color: 'warning',
        hint: this.conversionRate + '% de conversion',
        delta: null,
        route: { name: 'cotation-list' },
      },
      {
        icon: 'fad fa-sync', label: 'A renouveler',
        value: this.renewalKpis.pending, color: 'danger',
        hint: this.renewalKpis.total + ' echeances sur la periode',
        delta: null,
        route: { name: 'renewal-dashboard' },
      },
    ]
  }

  /**
   * Chaque cotation ne porte qu'un statut : celui de l'etape la plus avancee qu'elle a
   * atteinte. Le funnel se lit donc en cumulant les etapes suivantes, sinon chaque barre
   * n'affiche que les cotations bloquees a cette etape precise.
   */
  get funnelSteps() {
    const statusOrder = [
      { status: 'INCOMPLETE', label: 'Demarrees', color: 'gray-400' },
      { status: 'REACHABLE', label: 'Contactables', color: 'info' },
      { status: 'COMPARED', label: 'Comparees', color: 'primary' },
      { status: 'OFFER_SELECTED', label: 'Offre selectionnee', color: 'warning' },
      { status: 'PAYMENT_INITIALIZED', label: 'Paiement initie', color: 'info' },
      { status: 'VALIDATED', label: 'Validees', color: 'success' },
    ]
    const total = this.funnelTotal
    let remaining = total
    let previousReached: number | null = null

    return statusOrder.map((s, i) => {
      const reached = remaining
      const stepRate = previousReached && previousReached > 0
        ? Math.round((reached / previousReached) * 100)
        : null
      previousReached = reached
      remaining -= (this.funnelData[s.status] || 0)
      return {
        ...s,
        reached,
        count: this.funnelData[s.status] || 0,
        rate: total > 0 ? Math.round((reached / total) * 100) : 0,
        stepRate: i === 0 ? null : stepRate,
      }
    })
  }

  get conversionRate() {
    if (this.funnelTotal === 0) return '0.0'
    return ((this.funnelData['VALIDATED'] || 0) / this.funnelTotal * 100).toFixed(1)
  }

  get renewalItems() {
    const k = this.renewalKpis
    const max = Math.max(k.total, 1)
    return [
      { label: 'A traiter', value: k.pending, percent: (k.pending / max) * 100, color: 'danger' },
      { label: 'Devis en cours', value: k.inProgress, percent: (k.inProgress / max) * 100, color: 'warning' },
      { label: 'Perdus (churn)', value: k.churned, percent: (k.churned / max) * 100, color: 'gray-400' },
      { label: 'Renouveles', value: k.renewed, percent: (k.renewed / max) * 100, color: 'success' },
    ]
  }

  get renewalGauge() {
    return {
      chart: { type: 'radialBar', height: 180, fontFamily: 'inherit' },
      plotOptions: {
        radialBar: {
          hollow: { size: '65%' },
          track: { background: '#f1f1f4' },
          dataLabels: {
            name: { show: false },
            value: { fontSize: '28px', fontWeight: 700, color: '#3699FF', formatter: (val: number) => val + '%' }
          }
        }
      },
      colors: ['#3699FF'],
      stroke: { lineCap: 'round' as const },
    }
  }

  get alertsList() {
    const list: any[] = []
    if (this.coreAlerts.attestationErrors > 0) {
      list.push({
        icon: 'fad fa-exclamation-circle', color: 'danger',
        title: this.coreAlerts.attestationErrors + ' attestations ASACI en erreur',
        description: 'Des attestations digitales n\'ont pas pu etre generees.',
        route: null
      })
    }
    if (this.coreAlerts.expiringNoRenewal > 0) {
      list.push({
        icon: 'fad fa-clock', color: 'warning',
        title: this.coreAlerts.expiringNoRenewal + ' contrats expirent sans renouvellement',
        description: 'Contactez les clients pour initier le renouvellement.',
        route: { name: 'renewal-dashboard' }
      })
    }
    if (this.renewalKpis.primeEnRisque > 0 && this.renewalKpis.pending > 0) {
      list.push({
        icon: 'fad fa-hand-holding-dollar', color: 'warning',
        title: helper.minifyCurrency(this.renewalKpis.primeEnRisque) + ' de prime non renouvelee',
        description: this.renewalKpis.pending + ' contrats arrivent a echeance sans renouvellement engage.',
        route: { name: 'renewal-dashboard' }
      })
    }
    // On n'alerte que si le recouvrement decroche vraiment, sinon l'encart est permanent
    if (this.cashing.remaining > 0 && this.cashing.rate < 80) {
      list.push({
        icon: 'fad fa-file-invoice-dollar', color: 'danger',
        title: helper.minifyCurrency(this.cashing.remaining) + ' restent a encaisser',
        description: 'Seulement ' + this.cashing.rate + '% des primes emises sur la periode ont ete encaissees.',
        route: { name: 'payment-list' }
      })
    }
    if (this.coreAlerts.coveragesPending > 0) {
      list.push({
        icon: 'fad fa-shield-exclamation', color: 'info',
        title: this.coreAlerts.coveragesPending + ' couvertures en attente',
        description: 'Des couvertures PENDING existent sur des avenants actifs.',
        route: null
      })
    }
    if (this.renewalKpis.injoignable > 0) {
      list.push({
        icon: 'fad fa-phone-slash', color: 'danger',
        title: this.renewalKpis.injoignable + ' clients injoignables',
        description: this.renewalKpis.injoignableRate + '% des clients a renouveler n\'ont pas de telephone valide.',
        route: { name: 'renewal-dashboard' }
      })
    }
    if (this.funnelData['OFFER_SELECTED'] && this.funnelData['OFFER_SELECTED'] > 0) {
      list.push({
        icon: 'fad fa-hand-holding-dollar', color: 'warning',
        title: this.funnelData['OFFER_SELECTED'] + ' prospects avec offre selectionnee',
        description: 'Ces prospects ont choisi une offre mais n\'ont pas encore paye. Relancez-les !',
        route: { name: 'hot-leads', query: { status: 'OFFER_SELECTED' } }
      })
    }
    if (this.funnelData['COMPARED'] && this.funnelData['COMPARED'] > 10) {
      list.push({
        icon: 'fad fa-scale-balanced', color: 'info',
        title: this.funnelData['COMPARED'] + ' prospects ont compare des offres',
        description: 'Ils hesitent entre plusieurs offres. Un appel peut faire la difference.',
        route: { name: 'hot-leads', query: { status: 'COMPARED' } }
      })
    }
    if (this.funnelData['PAYMENT_INITIALIZED'] && this.funnelData['PAYMENT_INITIALIZED'] > 0) {
      list.push({
        icon: 'fad fa-credit-card', color: 'danger',
        title: this.funnelData['PAYMENT_INITIALIZED'] + ' paiements non finalises',
        description: 'Le prospect a initie le paiement mais ne l\'a pas termine.',
        route: { name: 'hot-leads', query: { status: 'PAYMENT_INITIALIZED' } }
      })
    }
    return list
  }

  // --- Methods ---

  get pipelineAutres(): number {
    const g = this.productionKpis.global
    return Math.max(0, g.total - g.projets - g.enAttente - g.emis)
  }

  get invoicePaid(): { count: number, amount: number } {
    const p = this.crmData.invoices.paid
    if (p && typeof p === 'object') return { count: p.count || 0, amount: p.amount || 0 }
    return { count: typeof p === 'number' ? p : 0, amount: 0 }
  }

  get invoiceUnpaid(): { count: number, amount: number } {
    const u = this.crmData.invoices.unpaid
    if (u && typeof u === 'object') return { count: u.count || 0, amount: u.amount || 0 }
    return { count: typeof u === 'number' ? u : 0, amount: 0 }
  }

  normalizeType(type: string): string {
    const key = (type || '').toUpperCase().trim().replace(/\s+/g, '_')
    // `avenant.type` n'est pas normalise en base : 4 orthographes coexistent.
    if (key === 'RENEW' || key === 'RENOUVELLEMENT') return 'RENOUVELLEMENT'
    return key
  }

  typeLabel(type: string): string {
    const labels: Record<string, string> = {
      'AFFAIRE_NOUVELLE': 'Affaire Nouvelle',
      'RENOUVELLEMENT': 'Renouvellement',
      'MODIFICATION': 'Modification',
      'RESILIATION': 'Resiliation'
    }
    return labels[this.normalizeType(type)] || type || '-'
  }

  typeBadge(type: string): string {
    const badges: Record<string, string> = {
      'AFFAIRE_NOUVELLE': 'badge-light-primary',
      'RENOUVELLEMENT': 'badge-light-info',
      'MODIFICATION': 'badge-light-warning',
      'RESILIATION': 'badge-light-danger'
    }
    return badges[this.normalizeType(type)] || 'badge-light'
  }

  /** Merge les doublons de type (ex: "Affaire Nouvelle", "AFFAIRE_NOUVELLE", "RENEW") */
  get mergedByType(): any[] {
    const map: Record<string, any> = {}
    for (const t of this.productionKpis.byType) {
      const key = this.normalizeType(t.type)
      if (map[key]) {
        map[key].total += t.total
        map[key].emis += t.emis
        map[key].primeEmise += (t.primeEmise || 0)
      } else {
        map[key] = { type: key, total: t.total, emis: t.emis, primeEmise: t.primeEmise || 0 }
      }
    }
    return Object.values(map)
  }

  // --- Data Loading ---

  async mounted() {
    await this.loadAll()
  }

  @Watch('interval', { deep: true })
  async onIntervalChange() {
    await this.loadAll()
  }

  /**
   * L'intercepteur axios ne rejette pas sur erreur serveur : sans ce garde-fou,
   * un appel casse se traduit par un ecran de zeros impossible a distinguer
   * d'une periode reellement vide.
   */
  private async run(label: string, task: () => Promise<boolean>) {
    try {
      const ok = await task()
      if (!ok) this.errors.push(label)
    } catch (e) {
      this.errors.push(label)
    }
  }

  async loadAll() {
    const start = new Date(this.interval.startDate)
    const end = new Date(this.interval.endDate)
    const dateFrom = this.toIsoDate(start)
    const dateTo = this.toIsoDate(end)
    const search = { start, end }

    this.loading = true
    this.errors = []

    const tasks = [
      this.run('Emissions', () => this.loadCoreDashboard(search)),
      this.run('Production', () => this.loadProductionKpis(dateFrom, dateTo)),
      this.run('Renouvellements', () => this.loadRenewalKpis(dateFrom, dateTo)),
      this.run('Alertes', () => this.loadAlerts()),
      this.run('Cotations', () => this.loadFunnel(search)),
    ]
    if (this.isManager) {
      tasks.push(this.run('CRM', () => this.loadCrmDashboard(search)))
    }
    await Promise.all(tasks)
    this.loading = false
  }

  async loadCoreDashboard(search: any): Promise<boolean> {
    let data: any = { search }
    if (!helper.empty(this.filter)) {
      data.search = Object.assign({}, search, this.filter)
    }
    const res = await api.post(api.core, 'data/dashboard', data)
    // res.data reste une chaine si la reponse n'est pas du JSON valide
    if (!res || !res.data || typeof res.data !== 'object') return false

    // Categories + Emissions
    this.categories = []
    this.emissionGlobale = 0
    this.emissionCount = 0
    const emissions = res.data.emissions || []
    const commissions = res.data.commissions || []

    ;(res.data.categories || []).forEach((a: any) => {
      const category = new BranchCategory(a)
      // Sans initialisation, une branche sans emission affiche une ligne vide
      // au lieu d'un zero explicite.
      category.branches.forEach((b: any) => {
        b.emission = 0
        b.count = 0
        b.commissions = 0
      })
      this.categories.push(category)
    })

    emissions.forEach((e: any) => {
      // Le total ne depend pas du rattachement a une branche active,
      // sinon une branche desactivee disparait silencieusement du total.
      this.emissionGlobale += e.primeTTC || 0
      this.emissionCount += e.count || 0
      this.categories.forEach((cat) => {
        cat.branches.forEach((branch: Branch) => {
          if (branch.id === e.branchId) {
            (branch as any).emission = e.primeTTC || 0;
            (branch as any).count = e.count || 0
          }
        })
      })
    })

    this.commissionGlobale = 0
    commissions.forEach((e: any) => {
      this.commissionGlobale += e.premium || 0
      this.categories.forEach((cat) => {
        cat.branches.forEach((branch: Branch) => {
          if (branch.id === e.branchId) {
            branch.commissions = e.premium || 0
          }
        })
      })
    })

    this.previousEmission = res.data.previous || this.previousEmission
    this.cashing = res.data.cashing || this.cashing

    // Portfolio donut
    const allBranches: { label: string, amount: number, share: number }[] = []
    // "Auto" existe en Particulier et en Entreprise : sans le prefixe, le donut
    // affiche deux fois la meme legende.
    const labelCount: Record<string, number> = {}
    this.categories.forEach(cat => {
      cat.branches.forEach((b: any) => {
        labelCount[b.label] = (labelCount[b.label] || 0) + 1
      })
    })
    this.categories.forEach(cat => {
      cat.branches.forEach((b: any) => {
        if (b.emission > 0) {
          const label = labelCount[b.label] > 1 ? cat.label + ' · ' + b.label : b.label
          allBranches.push({ label, amount: b.emission, share: 0 })
        }
      })
    })
    allBranches.sort((a, b) => b.amount - a.amount)
    const totalPortfolio = allBranches.reduce((sum, b) => sum + b.amount, 0)
    allBranches.forEach(b => {
      b.share = totalPortfolio > 0 ? Math.round((b.amount / totalPortfolio) * 100) : 0
    })
    this.portfolioBranches = allBranches

    this.portfolioChart = {
      series: allBranches.map(b => b.amount),
      labels: allBranches.map(b => b.label),
      colors: this.branchColors.slice(0, allBranches.length),
      chart: { type: 'donut', height: 200, fontFamily: 'inherit' },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              total: { show: true, label: 'Total', formatter: () => helper.minifyCurrency(this.emissionGlobale) }
            }
          }
        }
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { width: 2, colors: ['#fff'] },
      tooltip: { y: { formatter: (val: number) => helper.currencyFormat(val) } },
    }

    // Production chart from contracts data
    const contracts = res.data.contracts || []
    this.buildProductionChart(contracts)
    return true
  }

  /**
   * Determine la granularite selon la plage de dates :
   * < 31j → jour | 31j-6mois → semaine | > 6mois → mois
   */
  getGranularity(): 'day' | 'week' | 'month' {
    const start = new Date(this.interval.startDate)
    const end = new Date(this.interval.endDate)
    const days = Math.round((end.getTime() - start.getTime()) / 86400000)
    if (days <= 31) return 'day'
    if (days <= 180) return 'week'
    return 'month'
  }

  get granularityLabel(): string {
    const g = this.getGranularity()
    return g === 'day' ? 'Jour' : g === 'week' ? 'Semaine' : 'Mois'
  }

  /** Retourne le lundi de la semaine pour une date donnee (normalise a minuit) */
  getMonday(d: Date): Date {
    const result = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const day = result.getDay()
    const diff = day === 0 ? -6 : 1 - day
    result.setDate(result.getDate() + diff)
    return result
  }

  /** Formate YYYY-MM-DD en heure locale (toISOString bascule en UTC et decale d'un jour) */
  toIsoDate(d: Date): string {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
  }

  /** Retourne {sortKey, label} pour un bucket selon la granularite */
  getBucket(dateStr: string, granularity: 'day' | 'week' | 'month'): { sortKey: string, label: string } {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return { sortKey: dateStr, label: dateStr }
    const months = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec']

    if (granularity === 'month') {
      const sortKey = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
      return { sortKey, label: months[d.getMonth()] + ' ' + d.getFullYear() }
    }

    if (granularity === 'week') {
      const monday = this.getMonday(d)
      const sortKey = this.toIsoDate(monday)
      return { sortKey, label: monday.getDate() + ' ' + months[monday.getMonth()] }
    }

    // day
    const sortKey = this.toIsoDate(d)
    return { sortKey, label: helper.readable(d, 'dMy') }
  }

  buildProductionChart(contracts: any[]) {
    const granularity = this.getGranularity()
    const buckets: Record<string, { label: string, value: number, sortKey: string }> = {}

    contracts.forEach((c: any) => {
      const { sortKey, label } = this.getBucket(c.createdAt, granularity)
      if (!buckets[sortKey]) {
        buckets[sortKey] = { label, value: 0, sortKey }
      }
      buckets[sortKey].value += (c.primeTTC || 0)
    })

    // Trier par sortKey (chronologique)
    const sorted = Object.values(buckets).sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    const categories = sorted.map(b => b.label)
    const seriesData = sorted.map(b => Math.round(b.value))

    // Cumul : sur un dashboard de production, c'est la lecture qui compte
    let running = 0
    const cumulative = seriesData.map(v => (running += v))

    this.productionChart = {
      series: [
        { name: 'Prime TTC', type: 'area', data: seriesData },
        { name: 'Cumul', type: 'line', data: cumulative },
      ],
      chart: {
        type: 'line', height: 320,
        toolbar: { show: false },
        zoom: { enabled: true },
        fontFamily: 'inherit',
      },
      colors: ['#3699FF', '#50CD89'],
      fill: {
        type: ['gradient', 'solid'],
        gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 90, 100] }
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth' as const, width: [2.5, 2], dashArray: [0, 4] },
      legend: { show: true, position: 'top' as const, horizontalAlign: 'right' as const },
      grid: { borderColor: '#f1f1f4', strokeDashArray: 4 },
      xaxis: {
        categories,
        labels: {
          style: { colors: '#a1a5b7', fontSize: '11px' },
          rotate: -45,
          rotateAlways: categories.length > 15,
          hideOverlappingLabels: true,
        },
        axisBorder: { show: false }, axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: '#a1a5b7', fontSize: '11px' },
          formatter: (val: number) => helper.minifyCurrency(val)
        }
      },
      tooltip: {
        shared: true,
        y: { formatter: (val: number) => helper.currencyFormat(val) },
        x: { show: true }
      }
    }
  }

  async loadCrmDashboard(search: any): Promise<boolean> {
    const res = await api.post(api.core, 'data/dashboard/crm', {
      search: { ...search, includeLists: false }
    })
    if (!res || !res.data || typeof res.data !== 'object') return false
    this.crmData.contracts = res.data.contracts || this.crmData.contracts
    this.crmData.invoices = res.data.invoices || this.crmData.invoices
    return true
  }

  async loadProductionKpis(dateFrom: string, dateTo: string): Promise<boolean> {
    const res = await api.post(api.core, 'production/kpis', { dateFrom, dateTo })
    if (!res || !res.data || !res.data.kpis) return false
    this.productionKpis = Object.assign({}, this.productionKpis, res.data.kpis)
    return true
  }

  async loadRenewalKpis(dateFrom: string, dateTo: string): Promise<boolean> {
    const res = await api.post(api.core, 'renewal/kpis', { dateFrom, dateTo })
    if (!res || !res.data || !res.data.kpis) return false
    this.renewalKpis = { ...this.renewalKpis, ...res.data.kpis }
    return true
  }

  async loadAlerts(): Promise<boolean> {
    const res = await api.get(api.core, 'production/alerts')
    if (!res || !res.data || !res.data.alerts) return false
    this.coreAlerts = res.data.alerts
    return true
  }

  async loadFunnel(search: any): Promise<boolean> {
    const res = await api.post(api.form, 'data/dashboard/quotation', { search })
    if (!res || !res.data || typeof res.data !== 'object') return false

    this.funnelData = {}
    this.funnelTotal = 0
    this.funnelAborted = 0
    this.funnelExpired = 0

    const statuses = res.data.rgByStatus || []
    statuses.forEach((s: any) => {
      this.funnelData[s.status] = s.count
      if (s.status === 'ABORTED') {
        this.funnelAborted = s.count
      } else if (s.status === 'EXPIRED') {
        this.funnelExpired = s.count
      } else {
        this.funnelTotal += s.count
      }
    })
    return true
  }
}
</script>

<style scoped>
.dashboard :deep(.apexcharts-legend-text) {
  color: #a1a5b7 !important;
  font-size: 12px !important;
}
.flex-1 {
  flex: 1;
}
.card-hover {
  transition: transform .15s ease, box-shadow .15s ease;
}
.card-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 .5rem 1.5rem rgba(0, 0, 0, .1) !important;
}
.skeleton-line {
  height: 28px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f1f1f4 25%, #f9f9fb 50%, #f1f1f4 75%);
  background-size: 400% 100%;
  animation: skeleton 1.2s ease-in-out infinite;
}
@keyframes skeleton {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}
</style>
